import React, { ReactNode } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';

export type SidebarItem = {
  id: string;
  label: string;
  icon: ReactNode;
  count?: number;
};

export type SidebarSection = {
  label: string;
  items: SidebarItem[];
};

type SidebarProps = {
  activeItem: string;
  brand: ReactNode;
  isCollapsed?: boolean;
  onLogout?: () => void;
  onSelect: (itemId: string) => void;
  sections: SidebarSection[];
  user: {
    name: string;
    role: string;
    initials: string;
  };
};

function Sidebar({ activeItem, brand, isCollapsed, onLogout, onSelect, sections, user }: SidebarProps) {
  const [collapsedSections, setCollapsedSections] = React.useState<Record<string, boolean>>({});

  const toggleSection = (label: string) => {
    setCollapsedSections((current) => ({
      ...current,
      [label]: !current[label],
    }));
  };

  return (
    <Box className="sidebar" component="aside" aria-label="Primary navigation" data-collapsed={isCollapsed}>
      <div className="sb-brand">{brand}</div>
      <List className="sb-nav" component="nav" disablePadding>
        {sections.map((section) => (
          <Box className="sb-section" component="section" key={section.label}>
            <ListItemButton
              aria-expanded={!collapsedSections[section.label]}
              className="sb-nav-section"
              onClick={() => toggleSection(section.label)}
            >
              <ListItemText primary={section.label} />
              {collapsedSections[section.label]
                ? <ExpandMoreRoundedIcon fontSize="small" />
                : <ExpandLessRoundedIcon fontSize="small" />}
            </ListItemButton>
            <Collapse
              className="sb-section-collapse"
              in={isCollapsed || !collapsedSections[section.label]}
              timeout="auto"
              unmountOnExit
            >
              <List className="sb-section-list" component="div" disablePadding>
              {section.items.map((item) => (
              <Tooltip key={item.id} placement="right" title={isCollapsed ? item.label : ''}>
              <ListItemButton
                className={`sb-item${activeItem === item.id ? ' active' : ''}`}
                component="button"
                aria-label={isCollapsed ? item.label : undefined}
                onClick={() => onSelect(item.id)}
                selected={activeItem === item.id}
                title={isCollapsed ? item.label : undefined}
              >
                <ListItemIcon aria-hidden="true">{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
                {item.count !== undefined && item.count > 0 ? <span className="sb-count">{item.count}</span> : null}
              </ListItemButton>
              </Tooltip>
              ))}
              {section.label === 'Account' && onLogout ? (
                <Tooltip placement="right" title={isCollapsed ? 'Sign out' : ''}>
                  <ListItemButton
                    aria-label="Sign out"
                    className="sb-item sb-logout"
                    onClick={onLogout}
                  >
                    <ListItemIcon aria-hidden="true">
                      <LogoutRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Sign out" />
                  </ListItemButton>
                </Tooltip>
              ) : null}
              </List>
            </Collapse>
          </Box>
        ))}
      </List>
      <div className="sb-footer">
        <div className="sb-user">
          <Avatar className="sb-avatar">{user.initials}</Avatar>
          <span className="sb-user-info">
            <strong>{user.name}</strong>
            <span>{user.role}</span>
          </span>
        </div>
      </div>
    </Box>
  );
}

export default Sidebar;
